//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, IconButton, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import { theme } from '@react/components/theme'
import { companyProfileForAccountPut, companyProfilePut } from '@react/fetchers/companyProfileFetchers'
import { useOtChainState } from '@react/redux/store'
import { CompanyProfileCTO, CompanyProfileCTOSchema } from '@shared/CompanyProfileCTO'
import { logOT } from '@shared/loggingShared'
import { v4 as uuidv4 } from 'uuid'
import { EthAddr } from '@shared/SchemaTypes'
import CloseIcon from '@mui/icons-material/Close'
import { RoleEnum } from '@shared/Enums'

export default function CompanyProfileForm(props: {
  companyProfileCTO?: CompanyProfileCTO
  companyProfiles: CompanyProfileCTO[]
  accountAddr?: EthAddr
  closeCallback?: () => void
}): ReactElement {
  const { companyProfileCTO, accountAddr, closeCallback } = props

  const {
    cognitoUser,
    userConnection: { signedMessage, roleEnum },
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [isValid, setIsValid] = useState<boolean>(false)
  const [companyName, setCompanyName] = React.useState<string>(companyProfileCTO?.companyName ?? '')
  const [emails, setEmails] = React.useState<string[]>(companyProfileCTO?.emails ?? [])
  const [wallets, setWallets] = React.useState<string[]>(companyProfileCTO?.wallets ?? [])
  const [instructions, setInstructions] = React.useState<string>(companyProfileCTO?.instructions ?? '')
  const [validationMessage, setValidationMessage] = React.useState<string>('')

  const [uuid] = React.useState<string>(companyProfileCTO?.uuid ?? uuidv4())

  logOT.info('uuid')
  const handleOnSave = () => {
    console.log('handleOnSave')

    const handleOnSaveAsync = async () => {
      if (serverApiBaseUrl && (cognitoUser || location.hostname === 'localhost')) {
        setIsSubmitted(true)
        const companyProfileCTO: CompanyProfileCTO = {
          companyName,
          emails: emails.map((e) => e.trim()),
          wallets: wallets.map((e) => e.trim()),
          uuid,
          instructions,
        }
        await companyProfilePut(serverApiBaseUrl, companyProfileCTO, restAPIKey, cognitoUser)
        const event = new Event('modalClose')
        logOT.info('dispatching modalClose')
        window.dispatchEvent(event)
        closeCallback?.()
      } else if (serverApiBaseUrl && signedMessage && accountAddr) {
        const companyProfileCTO: CompanyProfileCTO = {
          companyName,
          emails: emails.map((e) => e.trim()),
          wallets: wallets.map((e) => e.trim()),
          uuid,
          instructions,
        }
        await companyProfileForAccountPut(serverApiBaseUrl, companyProfileCTO, restAPIKey, signedMessage, accountAddr)
        const event = new Event('modalClose')
        logOT.info('dispatching modalClose')
        window.dispatchEvent(event)
        closeCallback?.()
      }
    }
    handleOnSaveAsync()
  }
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
  }
  useEffect(() => {
    validate()
  }, [companyName, emails, wallets])

  function validate() {
    const companyProfileCTO: CompanyProfileCTO = {
      companyName,
      emails: emails.map((e) => e.trim()),
      wallets: wallets.map((e) => e.trim()).filter((e) => e.length > 0),
      uuid,
    }
    logOT.info('companyProfileCTO.wallets', companyProfileCTO.wallets)
    for (let i = 0; i < (props.companyProfiles?.length ?? 0); i++) {
      if (props.companyProfiles && props.companyProfiles[i].uuid !== companyProfileCTO.uuid) {
        logOT.info('Check duplicate addr in companyProfileCTO', props.companyProfiles[i])
        for (const addr of props.companyProfiles[i].wallets) {
          logOT.info('Check duplicate addr ', addr)
          if (companyProfileCTO.wallets.includes(addr)) {
            logOT.info('Wallet already exists ' + addr + ' in ' + props.companyProfiles[i].companyName)
            setValidationMessage('Wallet already exists ' + addr + ' in ' + props.companyProfiles[i].companyName)
            setIsValid(false)
            return
          }
        }
      }
    }
    try {
      logOT.info('companyProfileCTO', companyProfileCTO)

      const validation = CompanyProfileCTOSchema.validateSync(companyProfileCTO)
      setIsValid(true)
      setValidationMessage('')
      logOT.info('validation', validation)
    } catch (e: unknown) {
      const error = e as { message: string }
      setValidationMessage(error.message)
      logOT.info('validation error', e)
      setIsValid(false)
    }
  }
  return (
    <div data-testid="Company Profile Form">
      <Grid alignItems={'stretch'} container spacing={4} sx={{ flexGrow: 1, marginBottom: 8 }} py={2}>
        <Grid item xs={12}>
          <Typography>UUID2 = {uuid}</Typography>
        </Grid>

        <Grid item xs={12}>
          <InputLabel htmlFor="companyName">Company Name</InputLabel>
          <TextField
            id="companyName"
            data-testid="companyName"
            sx={{ width: '300px' }}
            variant="outlined"
            placeholder="Enter Company Name"
            value={companyName}
            onChange={(event) => {
              setCompanyName(event.target.value)
            }}
          />
        </Grid>
        {accountAddr && (
          <Grid item xs={12}>
            <Typography>Account Address = {accountAddr}</Typography>
          </Grid>
        )}
        {!accountAddr && (
          <Grid item xs={12}>
            <InputLabel htmlFor="wallets">Wallet Addresses</InputLabel>
            {wallets.map((wallet, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TextField
                  id={`wallet-${index}`}
                  data-testid={`wallet-${index}`}
                  sx={{ width: '300px' }}
                  variant="outlined"
                  value={wallet}
                  onChange={(event) => {
                    const newWallets = [...wallets]
                    newWallets[index] = event.target.value
                    setWallets(newWallets)
                  }}
                />
                <IconButton
                  onClick={() => {
                    const newWallets = wallets.filter((_, i) => i !== index)
                    setWallets(newWallets)
                  }}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              onClick={() => {
                setWallets([...wallets, ''])
              }}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Wallet
            </Button>
          </Grid>
        )}

        <Grid item xs={12}>
          <InputLabel htmlFor="emails">Email Addresses</InputLabel>
          {emails.map((email, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                id={`email-${index}`}
                data-testid={`email-${index}`}
                sx={{ width: '300px' }}
                variant="outlined"
                value={email}
                onChange={(event) => {
                  const newEmails = [...emails]
                  newEmails[index] = event.target.value
                  setEmails(newEmails)
                }}
              />
              <IconButton
                onClick={() => {
                  const newEmails = emails.filter((_, i) => i !== index)
                  setEmails(newEmails)
                }}
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            onClick={() => {
              setEmails([...emails, ''])
            }}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Email
          </Button>
        </Grid>
        {(roleEnum == RoleEnum.POOL_ADMIN || roleEnum == RoleEnum.BORROWER) && (
          <Grid item xs={12}>
            <InputLabel htmlFor="instructions">Instructions</InputLabel>
            <TextField
              id="instructions"
              data-testid="instructions"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={instructions}
              onChange={(event) => {
                setInstructions(event.target.value)
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography style={{ color: 'red' }}>{validationMessage}</Typography>
        </Grid>
      </Grid>
      <Button disabled={isSubmitted || !isValid} sx={sx} onClick={handleOnSave} variant={'contained'}>
        <Typography variant={'buttonBold'}>Save</Typography>
      </Button>
    </div>
  )
}
